import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  EventFormData,
  ICreateEventResponse,
  IGetEventParams,
  IGetEventResponse,
  IUpdateEventParams,
  IUpdateEventResponse,
  ISendReminderResponse,
  SendReminderPayload,
} from '../../interface/eventModel';
import { IErrorResponse } from '../../interface/main.interface';
import { eventService } from '../../services/event';

export const createEvent = createAsyncThunk<
  ICreateEventResponse,
  EventFormData,
  { rejectValue: IErrorResponse }
>('event/create', async (body, { rejectWithValue }) => {
  try {
    const response = await eventService.createEvent(body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const getEvent = createAsyncThunk<
  IGetEventResponse,
  IGetEventParams,
  { rejectValue: IErrorResponse }
>('event/get-one', async (params, { rejectWithValue }) => {
  try {
    const response = await eventService.getEvent(params);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const updateEvent = createAsyncThunk<
  IUpdateEventResponse,
  IUpdateEventParams,
  { rejectValue: IErrorResponse }
>('event/update', async (body, { rejectWithValue }) => {
  try {
    const response = await eventService.updateEvent(body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

/**
 * Send reminder to selected students for an event
 * Handles guardian validation and returns detailed feedback on sent/skipped reminders
 */
export const sendReminder = createAsyncThunk<
  ISendReminderResponse,
  { eventId: string; students: string[] },
  { rejectValue: IErrorResponse }
>('event/send-reminder', async ({ eventId, students }, { rejectWithValue }) => {
  try {
    const payload: SendReminderPayload = { students };
    const response = await eventService.sendReminder(eventId, payload);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.failed_to_send_reminders', i18n.t('toast.failed_to_send_reminders', 'Failed to send reminders')) });
    } else {
      return rejectWithValue({ message: err.message || i18n.t('toast.failed_to_send_reminders', 'Failed to send reminders') });
    }
  }
});

export const eventActions = {
  createEvent,
  getEvent,
  updateEvent,
  sendReminder,
};
